import React, { useState, useEffect } from "react";
import axios from "axios";
import { useBusinessPlan } from "../../App";
import { useCurrency } from "../../contexts/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tooltip as InfoTooltip } from "../ui/Tooltip";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Calendar
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExecutiveDashboard = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("ytd"); // ytd, qtd, mtd, 12m
  
  // Dashboard Data
  const [revenueHealth, setRevenueHealth] = useState(null);
  const [costHealth, setCostHealth] = useState(null);
  const [strategicHealth, setStrategicHealth] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [topWinners, setTopWinners] = useState([]);
  const [topConcerns, setTopConcerns] = useState([]);
  
  useEffect(() => {
    if (currentPlan?.id) {
      fetchDashboardData();
    }
  }, [currentPlan, timeRange]);
  
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [salesActuals, costActuals, products, costItems] = await Promise.all([
        axios.get(`${API}/actual-data/sales-v2/${currentPlan.id}`),
        axios.get(`${API}/actual-data/costs-v2/${currentPlan.id}`),
        axios.get(`${API}/v2/products-enhanced/${currentPlan.id}`),
        axios.get(`${API}/v3/cost-items/${currentPlan.id}`)
      ]);
      
      // Calculate Revenue Health
      calculateRevenueHealth(salesActuals.data, products.data);
      
      // Calculate Cost Health
      calculateCostHealth(costActuals.data, costItems.data);
      
      // Calculate Strategic Health
      calculateStrategicHealth(salesActuals.data, costActuals.data);
      
      // Build Trend Data
      buildTrendData(salesActuals.data, products.data);
      
      // Identify Winners & Concerns
      identifyPerformers(salesActuals.data, costActuals.data, products.data, costItems.data);
      
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const calculateRevenueHealth = (actuals, products) => {
    // Calculate total actual revenue
    const totalActualRevenue = actuals.reduce((sum, actual) => sum + (actual.revenue || 0), 0);
    
    // Calculate total planned revenue (simplified - based on products)
    const totalPlannedRevenue = actuals.reduce((sum, actual) => sum + (actual.planned_revenue || 0), 0);
    
    const variance = totalPlannedRevenue > 0 
      ? ((totalActualRevenue - totalPlannedRevenue) / totalPlannedRevenue * 100).toFixed(1)
      : 0;
    
    const status = variance >= -5 && variance <= 5 ? "on-track" 
                  : variance > 5 ? "exceeding" 
                  : "below";
    
    setRevenueHealth({
      planned: totalPlannedRevenue,
      actual: totalActualRevenue,
      variance: parseFloat(variance),
      status,
      count: actuals.length
    });
  };
  
  const calculateCostHealth = (actuals, costItems) => {
    // Calculate total actual costs
    const totalActualCost = actuals.reduce((sum, actual) => sum + (actual.actual_spent || 0), 0);
    
    // Calculate total budgeted costs
    const totalBudgetedCost = actuals.reduce((sum, actual) => sum + (actual.budgeted_amount || 0), 0);
    
    const variance = totalBudgetedCost > 0 
      ? ((totalActualCost - totalBudgetedCost) / totalBudgetedCost * 100).toFixed(1)
      : 0;
    
    const status = variance >= -5 && variance <= 5 ? "on-budget" 
                  : variance < -5 ? "under-budget" 
                  : "over-budget";
    
    setCostHealth({
      budgeted: totalBudgetedCost,
      actual: totalActualCost,
      variance: parseFloat(variance),
      status,
      count: actuals.length
    });
  };
  
  const calculateStrategicHealth = (salesActuals, costActuals) => {
    // Count objectives/initiatives from actuals
    const objectives = new Set();
    const initiatives = new Set();
    
    [...salesActuals, ...costActuals].forEach(actual => {
      if (actual.objective_id) objectives.add(actual.objective_id);
      if (actual.initiative_id) initiatives.add(actual.initiative_id);
    });
    
    // Simplified strategic health calculation
    setStrategicHealth({
      onTrack: Math.floor(objectives.size * 0.7),
      atRisk: Math.floor(objectives.size * 0.2),
      delayed: Math.floor(objectives.size * 0.1),
      total: objectives.size
    });
  };
  
  const buildTrendData = (actuals, products) => {
    // Group actuals by month
    const monthlyData = {};
    
    actuals.forEach(actual => {
      const month = actual.from_date ? actual.from_date.substring(0, 7) : actual.period;
      
      if (!monthlyData[month]) {
        monthlyData[month] = {
          month,
          actualRevenue: 0,
          plannedRevenue: 0
        };
      }
      
      monthlyData[month].actualRevenue += actual.revenue || 0;
      monthlyData[month].plannedRevenue += actual.planned_revenue || 0;
    });
    
    // Convert to array and sort
    const trendArray = Object.values(monthlyData)
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-12); // Last 12 months
    
    setTrendData(trendArray);
  };
  
  const identifyPerformers = (salesActuals, costActuals, products, costItems) => {
    // Calculate performance for each product
    const productPerformance = {};
    
    salesActuals.forEach(actual => {
      const key = actual.product_sku || actual.product_id;
      if (!productPerformance[key]) {
        productPerformance[key] = {
          name: actual.product_name,
          type: 'product',
          planned: 0,
          actual: 0,
          variance: 0
        };
      }
      productPerformance[key].planned += actual.planned_revenue || 0;
      productPerformance[key].actual += actual.revenue || 0;
    });
    
    // Calculate variances
    Object.keys(productPerformance).forEach(key => {
      const item = productPerformance[key];
      if (item.planned > 0) {
        item.variance = ((item.actual - item.planned) / item.planned * 100).toFixed(1);
      }
    });
    
    // Calculate performance for costs
    const costPerformance = {};
    
    costActuals.forEach(actual => {
      const key = actual.item_name;
      if (!costPerformance[key]) {
        costPerformance[key] = {
          name: actual.item_name,
          type: 'cost',
          category: actual.cost_category,
          budgeted: 0,
          actual: 0,
          variance: 0
        };
      }
      costPerformance[key].budgeted += actual.budgeted_amount || 0;
      costPerformance[key].actual += actual.actual_spent || 0;
    });
    
    // Calculate cost variances (negative is good for costs)
    Object.keys(costPerformance).forEach(key => {
      const item = costPerformance[key];
      if (item.budgeted > 0) {
        item.variance = ((item.actual - item.budgeted) / item.budgeted * 100).toFixed(1);
      }
    });
    
    // Combine and sort
    const allPerformers = [
      ...Object.values(productPerformance),
      ...Object.values(costPerformance)
    ];
    
    // Top Winners (positive variance for revenue, negative for costs)
    const winners = allPerformers
      .filter(p => (p.type === 'product' && p.variance > 0) || (p.type === 'cost' && p.variance < 0))
      .sort((a, b) => {
        const aScore = a.type === 'product' ? a.variance : Math.abs(a.variance);
        const bScore = b.type === 'product' ? b.variance : Math.abs(b.variance);
        return bScore - aScore;
      })
      .slice(0, 5);
    
    // Top Concerns (negative variance for revenue, positive for costs)
    const concerns = allPerformers
      .filter(p => (p.type === 'product' && p.variance < 0) || (p.type === 'cost' && p.variance > 0))
      .sort((a, b) => {
        const aScore = Math.abs(a.variance);
        const bScore = Math.abs(b.variance);
        return bScore - aScore;
      })
      .slice(0, 5);
    
    setTopWinners(winners);
    setTopConcerns(concerns);
  };
  
  const formatCurrency = (amount) => {
    if (!amount) return formatCurrencyContext(0, currentPlan?.currency);
    return formatCurrencyContext(amount, currentPlan?.currency);
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      'on-track': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'On Track' },
      'exceeding': { color: 'bg-blue-100 text-blue-800', icon: TrendingUp, label: 'Exceeding' },
      'below': { color: 'bg-red-100 text-red-800', icon: TrendingDown, label: 'Below Target' },
      'on-budget': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'On Budget' },
      'under-budget': { color: 'bg-blue-100 text-blue-800', icon: TrendingDown, label: 'Under Budget' },
      'over-budget': { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Over Budget' }
    };
    
    const config = statusConfig[status] || statusConfig['on-track'];
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <InfoTooltip content="Actual Performance Dashboard - Command center for business performance. Shows aggregated Revenue, Costs, and Strategic health at 30,000-foot view. Use for: Daily standup (5 min), Weekly reviews (15 min), Board meetings (30 min). Green = On track, Yellow = At risk, Red = Urgent action needed.">
            <h1 className="text-3xl font-bold text-gray-900">Actual Performance Dashboard</h1>
          </InfoTooltip>
          <p className="text-gray-600 mt-1">Performance overview and key metrics</p>
        </div>
        
        <InfoTooltip content="Time Period - Changes all metrics below. MTD (month to date) = Daily/weekly reviews. QTD (quarter to date) = Weekly leadership. YTD (year to date) = Monthly exec/board. 12M (last 12 months) = Investor updates, removes seasonality.">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mtd">Month to Date</SelectItem>
              <SelectItem value="qtd">Quarter to Date</SelectItem>
              <SelectItem value="ytd">Year to Date</SelectItem>
              <SelectItem value="12m">Last 12 Months</SelectItem>
            </SelectContent>
          </Select>
        </InfoTooltip>
      </div>
      
      {/* Health Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Revenue Health */}
        <Card>
          <CardHeader className="pb-3">
            <InfoTooltip content="Revenue Health - Top line performance summary. Green 'On Track' (≥ -5%) = Meeting targets. Yellow 'At Risk' (-5% to -15%) = Needs attention. Red 'Below Plan' (< -15%) = Critical, urgent action. First metric executives check every morning.">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <span className="flex items-center">
                  <DollarSign className="h-4 w-4 mr-2 text-green-600" />
                  Revenue Health
                </span>
                {revenueHealth && getStatusBadge(revenueHealth.status)}
              </CardTitle>
            </InfoTooltip>
          </CardHeader>
          <CardContent>
            {revenueHealth ? (
              <>
                <div className="space-y-2">
                  <div>
                    <InfoTooltip content="Planned revenue target from Strategic Framework for selected period">
                      <p className="text-xs text-gray-600">Plan</p>
                    </InfoTooltip>
                    <p className="text-xl font-bold">{formatCurrency(revenueHealth.planned)}</p>
                  </div>
                  <div>
                    <InfoTooltip content="Actual revenue recorded from Sales Actuals">
                      <p className="text-xs text-gray-600">Actual</p>
                    </InfoTooltip>
                    <p className="text-xl font-bold">{formatCurrency(revenueHealth.actual)}</p>
                  </div>
                  <div className="pt-2 border-t">
                    <InfoTooltip content="Variance: (Actual - Plan) / Plan × 100. Positive = Exceeded (good), Negative = Missed (investigate)">
                      <p className="text-xs text-gray-600">Variance</p>
                    </InfoTooltip>
                    <div className="flex items-center gap-2">
                      <p className={`text-2xl font-bold ${
                        revenueHealth.variance >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {revenueHealth.variance > 0 ? '+' : ''}{revenueHealth.variance}%
                      </p>
                      {revenueHealth.variance >= 0 ? (
                        <ArrowUpRight className="h-5 w-5 text-green-600" />
                      ) : (
                        <ArrowDownRight className="h-5 w-5 text-red-600" />
                      )}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Based on {revenueHealth.count} actual entries
                </p>
              </>
            ) : (
              <p className="text-gray-500 text-sm">No revenue data available</p>
            )}
          </CardContent>
        </Card>
        
        {/* Cost Health */}
        <Card>
          <CardHeader className="pb-3">
            <InfoTooltip content="Cost Health - Spending control summary. OPPOSITE of revenue: Green 'Under Budget' (≤ 0%) = Saved money (good!). Yellow 'Near Limit' (0% to +10%) = Watch closely. Red 'Over Budget' (> +10%) = Overspending (bad). Negative variance = Good for costs!">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
              <span className="flex items-center">
                <TrendingDown className="h-4 w-4 mr-2 text-red-600" />
                Cost Health
              </span>
              {costHealth && getStatusBadge(costHealth.status)}
            </CardTitle>
            </InfoTooltip>
          </CardHeader>
          <CardContent>
            {costHealth ? (
              <>
                <div className="space-y-2">
                  <div>
                    <InfoTooltip content="Budgeted costs from Cost Management for selected period">
                      <p className="text-xs text-gray-600">Budget</p>
                    </InfoTooltip>
                    <p className="text-xl font-bold">{formatCurrency(costHealth.budgeted)}</p>
                  </div>
                  <div>
                    <InfoTooltip content="Actual costs spent and recorded in Cost Actuals">
                      <p className="text-xs text-gray-600">Actual</p>
                    </InfoTooltip>
                    <p className="text-xl font-bold">{formatCurrency(costHealth.actual)}</p>
                  </div>
                  <div className="pt-2 border-t">
                    <InfoTooltip content="Cost Variance: (Actual - Budget) / Budget × 100. Negative = Under budget (good/green), Positive = Over budget (concern/red)">
                      <p className="text-xs text-gray-600">Variance</p>
                    </InfoTooltip>
                    <div className="flex items-center gap-2">
                      <p className={`text-2xl font-bold ${
                        costHealth.variance <= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {costHealth.variance > 0 ? '+' : ''}{costHealth.variance}%
                      </p>
                      {costHealth.variance <= 0 ? (
                        <ArrowDownRight className="h-5 w-5 text-green-600" />
                      ) : (
                        <ArrowUpRight className="h-5 w-5 text-red-600" />
                      )}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Based on {costHealth.count} actual entries
                </p>
              </>
            ) : (
              <p className="text-gray-500 text-sm">No cost data available</p>
            )}
          </CardContent>
        </Card>
        
        {/* Strategic Health */}
        <Card>
          <CardHeader className="pb-3">
            <InfoTooltip content="Strategic Health - Objective/initiative execution status. Shows on track, at risk, and off track counts. Use to monitor strategic execution alongside financial performance.">
              <CardTitle className="text-sm font-medium flex items-center">
                <Target className="h-4 w-4 mr-2 text-purple-600" />
                Strategic Health
              </CardTitle>
            </InfoTooltip>
          </CardHeader>
          <CardContent>
            {strategicHealth && strategicHealth.total > 0 ? (
              <>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <InfoTooltip content="Objectives/initiatives meeting or exceeding targets" showIcon={false}>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm">On Track</span>
                      </div>
                    </InfoTooltip>
                    <span className="text-2xl font-bold text-green-600">
                      {strategicHealth.onTrack}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <InfoTooltip content="Objectives/initiatives with issues or delays requiring attention" showIcon={false}>
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-yellow-600" />
                        <span className="text-sm">At Risk</span>
                      </div>
                    </InfoTooltip>
                    <span className="text-2xl font-bold text-yellow-600">
                      {strategicHealth.atRisk}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <InfoTooltip content="Objectives/initiatives significantly behind or missing targets" showIcon={false}>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-red-600" />
                        <span className="text-sm">Delayed</span>
                      </div>
                    </InfoTooltip>
                    <span className="text-2xl font-bold text-red-600">
                      {strategicHealth.delayed}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  {strategicHealth.total} strategic objectives tracked
                </p>
              </>
            ) : (
              <p className="text-gray-500 text-sm">No strategic data available</p>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Revenue Trend Chart */}
      <Card>
        <CardHeader>
          <InfoTooltip content="12-month trend: Actual (green area) vs Plan (blue line). Shows seasonal patterns and performance consistency. Use to identify growth trajectory and inflection points.">
            <CardTitle>Revenue vs Plan Trend (Last 12 Months)</CardTitle>
          </InfoTooltip>
          <CardDescription>Monthly comparison of actual revenue against plan</CardDescription>
        </CardHeader>
        <CardContent>
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => formatCurrencyContext(value, currentPlan?.currency, null, true)} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  labelFormatter={(label) => `Month: ${label}`}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="plannedRevenue" 
                  stackId="1"
                  stroke="#3b82f6" 
                  fill="#93c5fd" 
                  name="Planned Revenue"
                />
                <Area 
                  type="monotone" 
                  dataKey="actualRevenue" 
                  stackId="2"
                  stroke="#10b981" 
                  fill="#6ee7b7" 
                  name="Actual Revenue"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No trend data available</p>
              <p className="text-sm text-gray-400 mt-2">Record sales actuals to see trends</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Winners & Concerns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Winners */}
        <Card>
          <CardHeader>
            <InfoTooltip content="Best performers: Products exceeding revenue targets, costs under budget. Celebrate and replicate success factors.">
              <CardTitle className="flex items-center text-green-700">
                <TrendingUp className="h-5 w-5 mr-2" />
                Top 5 Winners 🎉
              </CardTitle>
            </InfoTooltip>
            <CardDescription>Best performing products and cost savings</CardDescription>
          </CardHeader>
          <CardContent>
            {topWinners.length > 0 ? (
              <div className="space-y-3">
                {topWinners.map((winner, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{winner.name}</p>
                      <p className="text-xs text-gray-600">
                        {winner.type === 'product' ? 'Revenue' : `Cost - ${winner.category}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <InfoTooltip content={winner.type === 'product' ? 'Revenue exceeded target by this %' : 'Cost under budget by this % (savings)'} showIcon={false}>
                        <p className="text-lg font-bold text-green-600">
                          {winner.type === 'product' ? '+' : ''}{Math.abs(winner.variance)}%
                        </p>
                      </InfoTooltip>
                      <p className="text-xs text-gray-600">
                        {winner.type === 'product' ? 'above target' : 'under budget'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-8">No winners data yet</p>
            )}
          </CardContent>
        </Card>
        
        {/* Top Concerns */}
        <Card>
          <CardHeader>
            <InfoTooltip content="Areas needing attention: Products missing revenue targets, costs over budget. Prioritize investigation and action on these items.">
              <CardTitle className="flex items-center text-red-700">
                <TrendingDown className="h-5 w-5 mr-2" />
                Top 5 Concerns ⚠️
              </CardTitle>
            </InfoTooltip>
            <CardDescription>Areas requiring attention and action</CardDescription>
          </CardHeader>
          <CardContent>
            {topConcerns.length > 0 ? (
              <div className="space-y-3">
                {topConcerns.map((concern, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{concern.name}</p>
                      <p className="text-xs text-gray-600">
                        {concern.type === 'product' ? 'Revenue' : `Cost - ${concern.category}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <InfoTooltip content={concern.type === 'product' ? 'Revenue fell short of target by this %' : 'Cost exceeded budget by this % (overspend)'} showIcon={false}>
                        <p className="text-lg font-bold text-red-600">
                          {Math.abs(concern.variance)}%
                        </p>
                      </InfoTooltip>
                      <p className="text-xs text-gray-600">
                        {concern.type === 'product' ? 'below target' : 'over budget'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-8">No concerns data yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
